import React, { useState, useEffect, useRef } from "react";
import styles from "./Carousel.module.css";

const Carousel = ({ events, isProfileFilled, Card, openModel, isMobile }) => {
    const [currentGroup, setCurrentGroup] = useState(0);
    const [displayCount, setDisplayCount] = useState(3);
    const [isAnimating, setIsAnimating] = useState(false);
    const carouselRef = useRef(null);
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);

    useEffect(() => {
        const handleResize = () => {
            setDisplayCount(window.innerWidth < 768 ? 1 : 3);
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleTouchStart = (e) => {
        if (!isMobile || e.target.closest('button, a')) return;
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchMove = (e) => {
        if (!isMobile || e.target.closest('button, a')) return;
        touchEndX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
        if (!isMobile || isAnimating || e.target.closest('button, a')) return;

        const diff = touchStartX.current - touchEndX.current;
        if (diff > 50) {
            nextGroup();
        } else if (diff < -50) {
            prevGroup();
        }
    };

    // Рассчитываем общее количество групп
    const totalGroups = Math.ceil(events.length / displayCount);

    // Рассчитываем индексы для текущей группы
    const getCurrentIndices = () => {
        const start = currentGroup * displayCount;
        let end = start + displayCount;

        if (end > events.length) {
            end = events.length;
            const newStart = Math.max(0, end - displayCount);
            return { start: newStart, end };
        }

        return { start, end };
    };

    const { start, end } = getCurrentIndices();
    const visibleEvents = events.slice(start, end);
    const isAtStart = currentGroup === 0;
    const isAtEnd = end >= events.length;

    const nextGroup = () => {
        if (isAnimating) return;

        setIsAnimating(true);
        if (!isAtEnd) {
            setCurrentGroup(prev => {
                const nextGroup = prev + 1;
                return nextGroup * displayCount < events.length ? nextGroup : prev;
            });
        } else {
            setCurrentGroup(0);
        }

        setTimeout(() => setIsAnimating(false), 500);
    };

    const prevGroup = () => {
        if (isAnimating) return;

        setIsAnimating(true);
        if (!isAtStart) {
            setCurrentGroup(prev => Math.max(prev - 1, 0));
        } else {
            setCurrentGroup(totalGroups - 1);
        }

        setTimeout(() => setIsAnimating(false), 500);
    };

    const goToGroup = (groupIndex) => {
        if (isAnimating) return;

        setIsAnimating(true);
        setCurrentGroup(Math.min(groupIndex, totalGroups - 1));
        setTimeout(() => setIsAnimating(false), 500);
    };

    // Рассчитываем смещение для анимации
    const getTransformValue = () => {
        if (events.length <= displayCount) return 'translateX(0)';

        // Для неполной последней группы
        if (currentGroup === totalGroups - 1 && events.length % displayCount !== 0) {
            const remainingItems = events.length % displayCount;
            const offset = (displayCount - remainingItems) * (100 / displayCount) / 1;
            return `translateX(calc(-${currentGroup * 100}% + ${offset}%))`;
        }

        return `translateX(-${currentGroup * 100}%)`;
    };

    return (
        <>
            <div className={styles.carouselContainer}>
                {!isMobile && events.length > displayCount && (
                    <button
                        className={styles.arrowButton}
                        onClick={prevGroup}
                        disabled={isAnimating}
                    >
                        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 18L9 12L15 6" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                )}

                <div
                    className={styles.carouselWrapper}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div
                        ref={carouselRef}
                        className={styles.carousel}
                        style={{
                            transform: getTransformValue(),
                            justifyContent: events.length <= displayCount ? 'center' : 'flex-start',
                        }}
                    >
                        {events.map((event, index) => (
                            <div
                                key={`${event.id}-${index}`}
                                className={styles.carouselItem}
                                style={{
                                    width: `${100 / displayCount}%`,
                                    flex: events.length <= displayCount ? '0 0 auto' : '1 0 auto'
                                }}
                            >
                                <Card event={event} isProfileFilled={isProfileFilled} openModel={openModel} />
                            </div>
                        ))}
                    </div>
                </div>

                {!isMobile && events.length > displayCount && (
                    <button
                        className={styles.arrowButton}
                        onClick={nextGroup}
                        disabled={isAnimating}
                    >
                        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 6L15 12L9 18" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                )}
            </div>

            {events.length > displayCount && (
                <div className={styles.indicators}>
                    {Array.from({ length: totalGroups }).map((_, index) => (
                        <button
                            key={index}
                            className={`${styles.indicator} ${currentGroup === index ? styles.active : ''}`}
                            onClick={() => goToGroup(index)}
                            disabled={isAnimating}
                        />
                    ))}
                </div>
            )}
        </>
    );
};

export default Carousel;